import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Box, Typography, Button, TextField } from '@mui/material';
import {
  defaultEventCategorySet,
  DefaultEventCategoryId,
  TaskItemForSchedule,
  TaskPriority,
} from '@demind-inc/core';
import {
  AccessTime,
  EditOutlined,
  LocalOfferOutlined,
  OutlinedFlag,
  CalendarToday,
  TipsAndUpdatesOutlined,
  Close,
} from '@mui/icons-material';
import { uniqBy } from 'lodash';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

import { getTaskToBeScheduled, validateScheduledTask, ValidationError } from './validate';
import { canLabelBeScheduled, getDefaultLables, schedulableLabels } from './constants';
import { useAuthContext } from '../../../../data-access';
import './ScheduledTaskInfoModal.scss';
import { DurationField, ToggleButtons } from '../../../common';
import { getCssVariable } from '../../../../utils';
import { PrioritySelect } from '../../../Priority';
import { getDefaultEventCategoryTitle } from '../../../DataView/i18n';

interface ScheduledTaskInfoModalProps {
  task: TaskItemForSchedule;
  taskId: string;
  targetDate: string;
  onItemChange: (id: string, task: TaskItemForSchedule) => void;
  onClose: () => void;
  open: boolean;
}

const defaultTask: TaskItemForSchedule = {
  taskId: '',
  name: '',
  duration: {
    amount: 30,
    unit: 'minute',
  },
  sortIndex: 0,
  labels: [],
  addedToCalendar: false,
  appFrom: 'lifestack',
  boardId: '',
  aiNote: '',
};

const hasChanged = (task: TaskItemForSchedule, localTask: TaskItemForSchedule) => {
  return (
    task.name !== localTask.name ||
    task.duration?.amount !== localTask.duration?.amount ||
    JSON.stringify(task.labels) !== JSON.stringify(localTask.labels) ||
    task.priority !== localTask.priority ||
    (task.aiNote || '') !== (localTask.aiNote || '')
  );
};

const colorTextBlack = getCssVariable('--color-black');
const colorPrimary = getCssVariable('--color-primary') || '#680ddb';

const ScheduledTaskInfoModal: React.FC<ScheduledTaskInfoModalProps> = ({
  task,
  taskId,
  targetDate,
  onItemChange,
  onClose,
  open,
}) => {
  const { t } = useTranslation();
  const [localTask, setLocalTask] = useState<TaskItemForSchedule>(defaultTask);
  const {
    user: { taskLabelsGroupId },
  } = useAuthContext();

  useEffect(() => {
    if (!task) {
      return;
    }
    setLocalTask(task);
  }, [task]);

  const { validationErrors, canSave } = useMemo(() => {
    const validationErrors = validateScheduledTask(localTask, targetDate);
    const canSave = validationErrors.length === 0 && hasChanged(task, localTask);

    return { validationErrors, canSave };
  }, [localTask, task, targetDate]);

  const existingNonSchedulableLabels = useMemo(
    () => localTask.labels?.filter((label) => !canLabelBeScheduled(label.name)) ?? [],
    [localTask.labels]
  );

  const getValidationError = (section: ValidationError['section']) =>
    validationErrors.find((error) => error.section === section);

  const handleSave = () => {
    if (!canSave) {
      return;
    }
    onItemChange(taskId, {
      ...task,
      ...localTask,
    });
    onClose();
  };

  const handleDurationChange = (value: { amount: number; unit: 'minute' }) => {
    setLocalTask({
      ...localTask,
      duration: {
        amount: value.amount,
        unit: 'minute',
      },
    });
  };

  const handleCategoryChange = (newCategory: (typeof defaultEventCategorySet)[0]) => {
    if (!newCategory.id) return;

    const newLabel = getDefaultLables(taskLabelsGroupId!).find(
      (label) => label.name === newCategory.name
    );

    if (!newLabel || !canLabelBeScheduled(newCategory.name)) {
      return;
    }

    const newLabels = uniqBy([...(existingNonSchedulableLabels || []), newLabel], 'name');
    setLocalTask({
      ...localTask,
      labels: newLabels,
    });
  };

  const handlePriorityChange = (priority?: TaskPriority) => {
    setLocalTask({
      ...localTask,
      priority,
    });
  };

  const handleAiNoteChange = (aiNote: string) => {
    setLocalTask({
      ...localTask,
      aiNote,
    });
  };

  const selectedCategoryName = useMemo(() => {
    const selectedLabel = localTask.labels?.find((label) =>
      schedulableLabels.some((category) => category.name === label.name)
    );
    return selectedLabel?.name || '';
  }, [localTask.labels]);

  const isAlreadyScheduled = useMemo(() => {
    return getTaskToBeScheduled(task, targetDate).hasScheduledAlready;
  }, [task.addedToCalendar, task.dueDateTime, targetDate]);

  const nameError = getValidationError('name');
  const durationError = getValidationError('duration');
  const labelsError = getValidationError('labels');

  return (
    <Modal open={open} onClose={onClose} className="scheduled-task-info-modal">
      <div className="scheduled-task-info-modal__content">
        <div className="scheduled-task-info-modal__header">
          <div className="scheduled-task-info-modal__header-spacer" />
          <h3 className="scheduled-task-info-modal__title">
            {t('task.scheduler.taskModal.adjustTask')}
          </h3>
          <button
            type="button"
            className="scheduled-task-info-modal__close-button"
            onClick={onClose}
            aria-label={t('data.common.close')}
          >
            <Close />
          </button>
        </div>

        <div className="scheduled-task-info-modal__body">
          {isAlreadyScheduled && (
            <div className="scheduled-task-info-modal__info-banner">
              <CalendarToday className="scheduled-task-info-modal__info-banner-icon" />
              <Typography className="scheduled-task-info-modal__info-banner-text" variant="caption">
                {t('task.scheduler.taskModal.alreadyScheduledForDate')}
              </Typography>
            </div>
          )}

          <div className="scheduled-task-info-modal__section-block">
            <div className="scheduled-task-info-modal__section-label">
              {t('task.scheduler.taskModal.taskName')}
            </div>
            <div
              className={clsx(
                'scheduled-task-info-modal__input-container',
                'scheduled-task-info-modal__input-container--disabled'
              )}
            >
              <EditOutlined
                className="scheduled-task-info-modal__inline-icon"
                sx={{ color: colorTextBlack }}
              />
              <Typography className="scheduled-task-info-modal__task-name">
                {localTask.name}
              </Typography>
            </div>
            {nameError && (
              <Typography className="scheduled-task-info-modal__error-text">
                {t(nameError.errorKey || 'task.scheduler.validation.taskNameRequired')}
              </Typography>
            )}
          </div>

          <div className="scheduled-task-info-modal__section-block">
            <div className="scheduled-task-info-modal__section-label">
              {t('routine.modal.duration')}
            </div>
            <div className="scheduled-task-info-modal__input-container">
              <AccessTime
                className="scheduled-task-info-modal__inline-icon"
                sx={{ color: colorTextBlack }}
              />
              <DurationField
                value={{
                  amount: localTask.duration?.amount || 0,
                  unit: 'minute',
                }}
                textStyle={{
                  fontSize: 15,
                  fontWeight: 600,
                  color: colorPrimary,
                }}
                onChange={handleDurationChange}
                showIcon={false}
              />
            </div>
            {durationError && (
              <Typography className="scheduled-task-info-modal__error-text">
                {t(durationError.errorKey || 'task.scheduler.validation.durationGreaterThanZero')}
              </Typography>
            )}
          </div>

          <div className="scheduled-task-info-modal__section-block">
            <div className="scheduled-task-info-modal__section-label">
              {t('task.scheduler.taskModal.type')}
            </div>
            <div className="scheduled-task-info-modal__input-container">
              <LocalOfferOutlined
                className="scheduled-task-info-modal__inline-icon"
                sx={{ color: colorTextBlack }}
              />
              <div className="scheduled-task-info-modal__field-content">
                <ToggleButtons
                  value={selectedCategoryName}
                  options={schedulableLabels.map((category) => ({
                    value: category.name,
                    label: getDefaultEventCategoryTitle(
                      t,
                      category.id as DefaultEventCategoryId,
                      category.name
                    ),
                  }))}
                  onChange={(value) => {
                    const category = schedulableLabels.find((cat) => cat.name === value);
                    if (category) {
                      handleCategoryChange(category);
                    }
                  }}
                  className="scheduled-task-info-modal__labels__toggle"
                />
                {!!existingNonSchedulableLabels.length && (
                  <Box className="scheduled-task-info-modal__labels__existing">
                    <Typography
                      variant="caption"
                      className="scheduled-task-info-modal__labels__existing__caption"
                    >
                      {t('task.scheduler.taskModal.existingLabels')}
                    </Typography>
                    <div className="scheduled-task-info-modal__labels__existing__item-list">
                      {existingNonSchedulableLabels.map((label) => (
                        <div
                          key={label.id}
                          className="scheduled-task-info-modal__labels__existing__item-list__item"
                        >
                          <Typography
                            variant="body2"
                            className="scheduled-task-info-modal__labels__existing__item-list__item__text"
                          >
                            {label.name}
                          </Typography>
                        </div>
                      ))}
                    </div>
                  </Box>
                )}
              </div>
            </div>
            {labelsError && (
              <Typography className="scheduled-task-info-modal__error-text">
                {t(labelsError.errorKey || 'task.scheduler.validation.labelRequired')}
              </Typography>
            )}
          </div>

          <div className="scheduled-task-info-modal__section-block">
            <div className="scheduled-task-info-modal__section-label">
              {t('task.scheduler.taskModal.priority')}
            </div>
            <div className="scheduled-task-info-modal__input-container">
              <OutlinedFlag
                className="scheduled-task-info-modal__inline-icon"
                sx={{ color: colorTextBlack }}
              />
              <PrioritySelect
                value={localTask.priority}
                onChange={handlePriorityChange}
                size="medium"
                className="scheduled-task-info-modal__priority-select"
              />
            </div>
          </div>

          <div className="scheduled-task-info-modal__section-block">
            <div className="scheduled-task-info-modal__section-label">
              {t('task.scheduler.taskModal.aiNote')}
            </div>
            <div
              className={clsx(
                'scheduled-task-info-modal__input-container',
                'scheduled-task-info-modal__input-container--top'
              )}
            >
              <TipsAndUpdatesOutlined
                className={clsx(
                  'scheduled-task-info-modal__inline-icon',
                  'scheduled-task-info-modal__inline-icon--top'
                )}
                sx={{ color: colorTextBlack }}
              />
              <TextField
                placeholder={t('task.modal.aiNotePlaceholder')}
                multiline
                minRows={2}
                maxRows={4}
                value={localTask.aiNote || ''}
                onChange={(e) => handleAiNoteChange(e.target.value)}
                variant="outlined"
                className="scheduled-task-info-modal__ai-note-textarea"
                fullWidth
              />
            </div>
          </div>
        </div>

        <div className="scheduled-task-info-modal__actions">
          <Button
            variant="text"
            onClick={onClose}
            className="scheduled-task-info-modal__cancel-button"
          >
            {t('task.common.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            className={clsx('scheduled-task-info-modal__save-button', {
              'scheduled-task-info-modal__save-button--disabled': !canSave,
            })}
            disabled={!canSave}
          >
            {t('task.common.save')}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default ScheduledTaskInfoModal;
